import { Environments } from "src/environmentInfo/Constants";
import { getEnvironment } from "src/environmentInfo/EnvironmentInfo";

/**
 * Per-environment base URL for the studio service.
 *
 * - LOCAL: `/studio-api` so the Create-React-App dev proxy
 *   (`src/setupProxy.js`) handles forwarding + SigV4 signing against the
 *   IAM-protected `/live/iam/api/*` route on the studio API. The proxy
 *   appends `/api` to the leaf path internally, so the local base
 *   intentionally omits it.
 * - BETA / GAMMA / PROD / ALPHA: absolute URL of the studio service custom
 *   domain, with the `/cognito/api` prefix so requests hit the
 *   Cognito-authorized API Gateway route. The studio service Lambda
 *   strips the `/cognito` prefix and resolves the leaf path against the
 *   same `/api/*` resolver as the IAM route, keeping the leaf paths
 *   symmetric across environments.
 *
 * Browser calls in deployed environments carry the Cognito ID token in
 * the `Authorization` header; the studio's `CognitoUserPoolsAuthorizer`
 * validates it against the Nucleus user pool ARN configured in the
 * studio service CDK.
 *
 * Gamma and Prod studio domains are entered as the expected DNS pattern
 * and will be live once the studio team deploys those stages. Until then,
 * the form studio page should stay gated behind the `enableFormStudio`
 * feature flag in those environments.
 */
const STUDIO_API_BASE_BY_ENV: Record<string, string> = {
  [Environments.LOCAL]: "/studio-api",
  [Environments.ALPHA]:
    "https://beta.forms-studio.fintech.amazon.dev/cognito/api",
  [Environments.BETA]:
    "https://beta.forms-studio.fintech.amazon.dev/cognito/api",
  [Environments.GAMMA]:
    "https://gamma.forms-studio.fintech.amazon.dev/cognito/api",
  [Environments.PROD]: "https://forms-studio.fintech.amazon.dev/cognito/api",
};

const getStudioApiBase = (): string =>
  STUDIO_API_BASE_BY_ENV[getEnvironment()] ?? "/studio-api";

/**
 * Studio API base URL for the current environment. Exported for tests and
 * for the dev proxy code path; production code should use `STUDIO_API_URLS`.
 */
export const STUDIO_API_BASE = getStudioApiBase();

/**
 * Single source of URLs for the form studio backend. Feeds the adapter
 * factories in `FormStudioView`. The studio component itself only sees
 * the resolved adapters, never these URLs.
 */
export const STUDIO_API_URLS = {
  llmUrl: `${STUDIO_API_BASE}/amazon-llm`,
  healthUrl: `${STUDIO_API_BASE}/health`,
  saveUrl: `${STUDIO_API_BASE}/save-schema`,
  listUrl: `${STUDIO_API_BASE}/schemas`,
  getUrl: `${STUDIO_API_BASE}/schemas`,
};

export type SchemaFolder = "DRAFT" | "PENDING_APPROVAL" | "APPROVED";

export const STUDIO_BROWSER_FOLDERS: { id: SchemaFolder; label: string }[] = [
  { id: "DRAFT", label: "Draft" },
  { id: "PENDING_APPROVAL", label: "Pending Approval" },
  { id: "APPROVED", label: "Approved" },
];

export const SCHEMA_NAME_PATTERN = /^[a-zA-Z0-9_-]{1,128}$/;
